import {
  APP_PART_TYPES,
  COLUMN_WIDTH,
  DEFAULT_WIDTH_COL,
} from "@lhd/features/Apps/AppForm/constants";
import { AppPartType, TItemAttachment } from "@lhd/features/Apps/AppForm/types";
import { getDisplayText } from "@lhd/features/Apps/AppList/ManagementAppSheet/ManagementAppSheetList/utility";
import { HIDDENS_COLUMNS_SHEET } from "@lhd/features/Apps/constants";
import { TYPE_CELLS } from "./contants";
import { CellCustomType } from "./type";
import { stripHtml } from "@lhd/utility";
import dayjs from "dayjs";
import { DATE_TIME_FORMAT } from "@lhd/constants";
import { AppRecordValueV2, AppSelectionList } from "@lhd/types";
import { Row } from "@silevis/reactgrid";
import {
  getPrimaryCompanyNameDisplayText,
  validateEmail,
  validatePhone,
  validateUrl,
} from "@lhd/features/Apps/utils";
import { getOptionsFormAppPart, getTextSection } from "../../fieldUtils";
import { MIN_ROW_HEIGHT } from "../../fields/TableSheet/utils";
import { HIDDEN_FILTER_APP_PART_TYPES, SEPARATE } from "../../constants";

export const getColumns = (
  appPart?: AppPartType[],
  isWidthByConfig?: boolean,
  appType?: string,
) => {
  return (
    appPart
      ?.filter((appPartItem) => {
        return !HIDDENS_COLUMNS_SHEET.includes(appPartItem.app_part_type);
      })
      ?.map((ap) => {
        let w = ap.item_config_data?.w || 1;
        if (ap.app_part_type === APP_PART_TYPES.NUMBER) {
          w = 7;
        }
        const label = ap.label_text || ap.name || "";
        const isDev = localStorage.getItem("isDev") === "true";
        let width = isWidthByConfig
          ? w * COLUMN_WIDTH
          : ap.width || DEFAULT_WIDTH_COL;
        if (label === "章条項" || label === "章条项") {
          width = Math.round((width * 2) / 3);
        }
        const fieldCommons = {
          columnId: ap.id?.toString() || "",
          copy_from_app_part_id: ap?.copy_from_app_part_id,
          label: ap.is_hide_name
            ? ""
            : isDev
            ? `${ap.id}-${ap.app_part_type}`
            : ap.name,
          width,
          type: "text",
          resizable: false,
          appPart: ap,
          required: appType === "role_permissions" && ap.name === "ステータス",
          hiddenFilter: HIDDEN_FILTER_APP_PART_TYPES.includes(
            ap.app_part_type as any,
          ),
        };
        switch (ap.app_part_type) {
          case APP_PART_TYPES.NUMBER:
            return {
              ...fieldCommons,
            };

          default:
            return {
              ...fieldCommons,
              // type: "textCellCustom",
            };
        }
      }) || []
  );
};

export const getTypeByAppPart = (appPart: AppPartType) => {
  switch (appPart.app_part_type) {
    case APP_PART_TYPES.NUMBER:
      return TYPE_CELLS.NUMBER_CELL;

    case APP_PART_TYPES.ATTACHMENT:
      return TYPE_CELLS.ATTACHMENT_CELL;

    case APP_PART_TYPES.TIME:
      return TYPE_CELLS.TIME_CELL;

    case APP_PART_TYPES.DATE_AND_TIME:
      return TYPE_CELLS.DATE_TIME_CELL;

    case APP_PART_TYPES.DATE:
      return TYPE_CELLS.DATE_CELL;

    case APP_PART_TYPES.WORKER_SELECTION:
    case APP_PART_TYPES.DEPARTMENT_SELECTION:
    case APP_PART_TYPES.DIVISION_SELECTION:
    case APP_PART_TYPES.BRANCH_SELECTION:
    case APP_PART_TYPES.ORGANIZATION_SELECTION:
    case APP_PART_TYPES.COMPANY_LOCATION_SELECTION:
    case APP_PART_TYPES.REPRESENTATIVE_SELECTION:
    case APP_PART_TYPES.COMPANY_PHONE_NUMBER_SELECTION:
    case APP_PART_TYPES.POSITION_SELECTION:
    case APP_PART_TYPES.CUSTOMER_SELECTION:
      return TYPE_CELLS.SELECT_CELL;

    case APP_PART_TYPES.RICH_TEXT:
      return TYPE_CELLS.RICH_TEXT_CELL;

    case APP_PART_TYPES.RADIO_BUTTON:
      return TYPE_CELLS.RADIO_CELL;

    case APP_PART_TYPES.CHECK_BOX:
      return TYPE_CELLS.CHECK_BOX_CELL;

    case APP_PART_TYPES.MULTI_CHOICE:
      return TYPE_CELLS.MULTI_CHOICE_CELL;

    case APP_PART_TYPES.DROPDOWN:
      return TYPE_CELLS.DROP_DOWN_CELL;

    case APP_PART_TYPES.LINK:
      return TYPE_CELLS.LINK_CELL;
    case APP_PART_TYPES.TEXT_AREA:
      return TYPE_CELLS.TEXT_AREA_CELL;
    case APP_PART_TYPES.PRIMARY_COMPANY_NAME:
    case APP_PART_TYPES.TEXT:
      return TYPE_CELLS.TEXT_CELL;
    default:
      return "text";
  }
};

export const getValueFieldByAppPart = (
  appPart: AppPartType,
  record?: AppRecordValueV2,
) => {
  switch (appPart.app_part_type) {
    case APP_PART_TYPES.ATTACHMENT:
      return {
        text: record?.value,
        valueSubmitAttachment: record?.value,
      };
    case APP_PART_TYPES.TEXT_AREA:
    case APP_PART_TYPES.TEXT:
      return {
        text: record?.value,
      };
    default:
      return {
        text: record?.value,
        valueSubmit: record?.value,
      };
  }
};

export const getMaxHeightByAppPart = (appPart: AppPartType) => {
  switch (appPart.app_part_type) {
    case APP_PART_TYPES.DROPDOWN:
      return MIN_ROW_HEIGHT;
    default:
      return undefined;
  }
};

/**
 * Normalize a date/time string into Japanese style format "YYYY年MM月DD日 HH:mm"
 * Supports:
 * - Japanese style: "2019年4月30日 (火曜)", "2019年4月30日"
 * - Standard formats: "2019-4-3 14:5", "2019/4/3 9:3"
 * - If no time is provided, defaults to "00:00"
 * @param input - raw date/time string
 * @returns normalized date/time string or empty string if invalid
 */
function normalizeDateTime(input: string): string {
  if (!input) return "";
  // Trim leading/trailing whitespace
  let cleaned = input.trim();
  const timeInput = dayjs(input);
  if (timeInput.isValid()) {
    return timeInput.format(DATE_TIME_FORMAT.DATE_TIME);
  }
  // Remove any weekday information in parentheses (e.g., (火曜))
  cleaned = cleaned.replace(/\(.*?\)/g, "").trim();

  // Split into date and time parts
  const cleanedSplit = cleaned.split(" ");
  let datePart = cleanedSplit[0];
  const timePart = cleanedSplit[1];

  // Convert Japanese year/month/day characters to /
  if (/[年月日]/.test(datePart)) {
    datePart = datePart.replace(/年|月/g, "/").replace(/日/g, "");
  }

  // Replace - with / for standardization
  datePart = datePart.replace(/-/g, "/");

  // Split into year, month, day and pad with 0 if necessary
  const dateParts = datePart.split("/").map((p) => p.padStart(2, "0"));
  if (dateParts.length !== 3) return "";

  const [year, month, day] = dateParts;

  // Default time values
  let hour = "00";
  let minute = "00";

  if (timePart) {
    const timeParts = timePart.split(":").map((p) => p.padStart(2, "0"));
    hour = timeParts[0] || "00";
    minute = timeParts[1] || "00";
  }

  // Construct normalized datetime in Japanese format
  const normalizedDateTime = `${year}/${month}/${day} ${hour}:${minute}`;

  // Validate using dayjs
  if (
    !dayjs(
      `${year}-${month}-${day} ${hour}:${minute}`,
      "YYYY-MM-DD HH:mm",
      true,
    ).isValid()
  )
    return "";

  return normalizedDateTime;
}

const getValueTextSelection = (
  appPart: AppPartType,
  valueSubmit: number[],
  selectionDataList: AppSelectionList,
) => {
  try {
    return {
      text: getTextSection(valueSubmit, appPart, selectionDataList) || "",
      valueSubmit: valueSubmit,
    };
  } catch (e) {
    console.log(e);
    return {
      text: "",
      valueSubmit: valueSubmit,
    };
  }
};

const getValueTextMultiChoice = (appPart: AppPartType, valueSubmit: any) => {
  const options = getOptionsFormAppPart(appPart);
  try {
    let values: any[] = [];
    if (Array.isArray(valueSubmit)) {
      values = valueSubmit;
    } else {
      const jsonValues = valueSubmit || "[]";
      values =
        typeof jsonValues === "string" ? JSON.parse(jsonValues) : jsonValues;
    }
    const valueMap = (Array.isArray(values) ? values : [])
      .map((value: any) => {
        const option = options.find(
          (option) =>
            option.value?.toString() === value?.toString() ||
            option.label?.toString() === value?.toString(),
        );
        return option?.label;
      })
      .filter((x: any) => !!x);
    return {
      text: valueMap.join(SEPARATE),
      valueSubmit: Array.isArray(valueSubmit)
        ? JSON.stringify(valueSubmit)
        : valueSubmit,
    };
  } catch (e) {
    //value is a string such as "value1,value2,value3", expect valueSubmit is a JSON "[1,2,3]"
    const valuesText = Array.isArray(valueSubmit)
      ? valueSubmit
      : typeof valueSubmit === "string"
      ? valueSubmit.split(SEPARATE)
      : [];
    const optionValues = valuesText
      .map((value: any) => {
        const v = String(value || "").trim();
        return options.find(
          (option) => option?.label === v || option?.value?.toString() === v,
        );
      })
      .filter((option) => !!option);
    const finalSubmit = JSON.stringify(optionValues.map((o) => o?.value));
    return {
      text: optionValues.map((op) => op?.label).join(SEPARATE),
      valueSubmit: finalSubmit,
    };
  }
};

const getValueTextSingle = (appPart: AppPartType, valueSubmit: any) => {
  const options = getOptionsFormAppPart(appPart);
  const normalizedValue = Array.isArray(valueSubmit)
    ? valueSubmit[0]
    : valueSubmit;
  try {
    const option = options.find((option) => option.value === normalizedValue);
    return {
      text: (option?.label as string) ?? "",
      valueSubmit: option?.value || "",
    };
  } catch (e) {
    //value is a string such as "value1,value2,value3", expect valueSubmit is a JSON "[1,2,3]"
    const option = options.find(
      (option) =>
        option.label ===
        (typeof normalizedValue === "string"
          ? normalizedValue.trim()
          : normalizedValue),
    );
    return {
      text: (option?.label as string) ?? "",
      valueSubmit: option?.value || "",
    };
  }
};

const getValueTextCheckBox = (appPart: AppPartType, valueSubmit: any) => {
  const options = getOptionsFormAppPart(appPart);

  let rawValues: string[] = [];
  if (Array.isArray(valueSubmit)) {
    rawValues = valueSubmit
      .map((v) => (v !== null && v !== undefined ? String(v).trim() : ""))
      .filter(Boolean);
  } else if (typeof valueSubmit === "string") {
    const trimmed = valueSubmit.trim();
    if (trimmed) {
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            rawValues = parsed
              .map((v) =>
                v !== null && v !== undefined ? String(v).trim() : "",
              )
              .filter(Boolean);
          }
        } catch {
          // If JSON parse fails (e.g., single-quoted array string), strip brackets and split
          const inner = trimmed.slice(1, -1);
          rawValues = inner
            .split(SEPARATE)
            .map((v) =>
              v
                .trim()
                .replace(/^['"]|['"]$/g, "")
                .trim(),
            )
            .filter(Boolean);
        }
      }
      if (rawValues.length === 0) {
        rawValues = trimmed
          .split(SEPARATE)
          .map((v) => v.trim())
          .filter(Boolean);
      }
    }
  } else if (valueSubmit !== null && valueSubmit !== undefined) {
    rawValues = [String(valueSubmit).trim()].filter(Boolean);
  }

  // Deduplicate raw values
  const uniqueRawValues = Array.from(new Set(rawValues));
  const labels: string[] = [];
  const submitValues: string[] = [];

  uniqueRawValues.forEach((val) => {
    const option = options.find((opt) => {
      const optValStr =
        opt.value !== null && opt.value !== undefined
          ? String(opt.value).trim()
          : "";
      const optLabelStr =
        opt.label !== null && opt.label !== undefined
          ? String(opt.label).trim()
          : "";
      return optValStr === val || optLabelStr === val;
    });

    if (option) {
      if (option.label !== undefined && option.label !== null) {
        labels.push(String(option.label));
      }
      if (option.value !== undefined && option.value !== null) {
        submitValues.push(String(option.value));
      }
    } else {
      labels.push(val);
      submitValues.push(val);
    }
  });

  return {
    text: labels.join(SEPARATE),
    valueSubmit: submitValues.join(SEPARATE),
  };
};

const normalizeAttachmentSubmitValue = (
  data: string | CellCustomType | TItemAttachment[],
): TItemAttachment[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === "string") {
    const trimmed = data.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  const v = (data as CellCustomType).valueSubmitAttachment;
  return Array.isArray(v) ? v : [];
};

export const getDataTextByAppPart = (
  appPart: AppPartType,
  data: any,
  selectionDataList: AppSelectionList,
) => {
  let textRaw: any = "";
  if (typeof data === "string" || !data) {
    textRaw = data;
  } else if (Array.isArray(data)) {
    textRaw = data;
  } else if (
    (appPart.app_part_type === APP_PART_TYPES.NUMBER ||
      appPart.app_part_type === APP_PART_TYPES.TEXT ||
      appPart.app_part_type === APP_PART_TYPES.RICH_TEXT) &&
    typeof data === "object"
  ) {
    textRaw = (data as CellCustomType).text || "";
  } else if (typeof data === "object") {
    // Handle CellCustomType: valueSubmit can be string | string[] | number[] | TItemAttachment[]
    const valueSubmit = data.valueSubmit;
    if (valueSubmit !== undefined && valueSubmit !== null) {
      textRaw = valueSubmit;
    } else {
      textRaw = data.text || "";
    }
  }

  switch (appPart.app_part_type) {
    case APP_PART_TYPES.RICH_TEXT:
      return {
        text: stripHtml(textRaw) ?? "",
        valueSubmit: textRaw,
      };

    case APP_PART_TYPES.TEXT:
      return {
        text: typeof textRaw === "string" ? textRaw : String(textRaw ?? ""),
      };

    case APP_PART_TYPES.PRIMARY_COMPANY_NAME:
      return {
        text: getPrimaryCompanyNameDisplayText() ?? "",
      };

    case APP_PART_TYPES.WORKER_SELECTION:
    case APP_PART_TYPES.DEPARTMENT_SELECTION:
    case APP_PART_TYPES.DIVISION_SELECTION:
    case APP_PART_TYPES.BRANCH_SELECTION:
    case APP_PART_TYPES.ORGANIZATION_SELECTION:
    case APP_PART_TYPES.COMPANY_LOCATION_SELECTION:
    case APP_PART_TYPES.REPRESENTATIVE_SELECTION:
    case APP_PART_TYPES.COMPANY_PHONE_NUMBER_SELECTION:
    case APP_PART_TYPES.POSITION_SELECTION:
    case APP_PART_TYPES.CUSTOMER_SELECTION:
      return getValueTextSelection(
        appPart,
        data.valueSubmit,
        selectionDataList,
      );

    case APP_PART_TYPES.DATE:
    case APP_PART_TYPES.TIME:
    case APP_PART_TYPES.DATE_AND_TIME:
      return {
        text: normalizeDateTime(textRaw),
        displayText: getDisplayText(appPart, normalizeDateTime(textRaw)),
      };

    case APP_PART_TYPES.NUMBER:
      return {
        text: (textRaw ? getDisplayText(appPart, textRaw) : "") ?? "",
        valueSubmit: textRaw,
      };

    case APP_PART_TYPES.RADIO_BUTTON:
    case APP_PART_TYPES.DROPDOWN:
      return getValueTextSingle(appPart, textRaw);

    case APP_PART_TYPES.MULTI_CHOICE:
      return getValueTextMultiChoice(appPart, textRaw);
    case APP_PART_TYPES.CHECK_BOX:
      return getValueTextCheckBox(appPart, textRaw);

    case APP_PART_TYPES.ATTACHMENT:
      return {
        text: "",
        valueSubmitAttachment: normalizeAttachmentSubmitValue(data),
      };

    case APP_PART_TYPES.TEXT_AREA:
      return {
        text: typeof textRaw === "string" ? textRaw : String(textRaw ?? ""),
      };

    default:
      return {
        text: typeof textRaw === "string" ? textRaw : String(textRaw ?? ""),
        displayText: textRaw ? getDisplayText(appPart, textRaw) : "",
      };
  }
};

export const validator = (row: Row<CellCustomType>) => {
  let isInValidRow = false;
  // const messageError: CellCustomType["messageError"] = {};

  const validatedCells = row.cells.map((cell) => {
    let isInValid = false;
    if (cell.appPart) {
      const {
        max_num_char, // Maximum allowed characters
        min_num_char, // Minimum required characters
        is_required_field, // Whether the field is mandatory
        app_part_type,
      } = cell.appPart || {};
      // 1 Rule: required field
      if (!isInValid && is_required_field) {
        isInValid = !cell.text;
        //messageError.required = `${cell.appPart.label_text}は必須項目です。`;
      }

      // 2️⃣ Rule: Minimum character length
      if (
        typeof min_num_char === "number" &&
        Number(cell.valueSubmit) < min_num_char
      ) {
        //messageError.min = `少なくとも${min_num_char}文字を入力してください。`
        isInValid = true;
      }

      // 3️⃣ Rule: Maximum character length
      if (
        typeof max_num_char === "number" &&
        Number(cell.valueSubmit) > max_num_char
      ) {
        // messageError.max = `${max_num_char}文字以内で入力してください。`;
        isInValid = true;
      }

      if (
        typeof max_num_char === "number" &&
        Number(cell.valueSubmit) > max_num_char
      ) {
        // messageError.max = `${max_num_char}文字以内で入力してください。`;
        isInValid = true;
      }

      //-validate by app_part_type:

      //  + LINK
      if (!isInValid && app_part_type === APP_PART_TYPES.LINK && cell.text) {
        const link_type = cell.appPart.item_config_data.link_type;

        if (link_type === "phone_number" && !validatePhone(cell.text)) {
          // phone_number
          isInValid = true;
        } else if (link_type === "email" && !validateEmail(cell.text)) {
          // email
          isInValid = true;
        } else if (!validateUrl(cell.text)) {
          // url
          isInValid = isInValid = true;
        }
      }
    }

    if (!isInValidRow && isInValid) isInValidRow = true;

    const classes = (cell.className || "")
      .split(" ")
      .filter((c) => c && c !== "invalid");
    if (isInValid) {
      classes.push("invalid");
    }
    const finalClassName = classes.join(" ");

    return {
      ...cell,
      isInValid: isInValid,
      className: finalClassName,
      //messageError,
    };
  });

  return {
    isInValidRow: isInValidRow,
    row: { ...row, cells: validatedCells },
  };
};

export const getObjectId = (data: any) => {
  if (data?.id) {
    return { id: data.id };
  }
  return {};
};

export const getStyle = (isOdd: boolean, cellStyle?: React.CSSProperties) => {
  const style = {
    alignItems: "baseline",
    color: "#000",
    background: isOdd ? "#efefef" : "#fff",
  };
  if (cellStyle?.color) {
    style.color = cellStyle?.color as string;
  }

  if (cellStyle?.background) {
    style.background = cellStyle?.background as string;
  }

  return { ...cellStyle, ...style };
};

export const handleCheckChangeAttachment = (
  current?: TItemAttachment[],
  pre?: TItemAttachment[],
) => {
  const textCurrentValue = current
    ?.map(
      (itemAttachment) =>
        itemAttachment.url + !!itemAttachment.is_allow_download_attachment,
    )
    .join(",");

  const textPreValue = pre
    ?.map(
      (itemAttachment) =>
        itemAttachment.url + !!itemAttachment.is_allow_download_attachment,
    )
    .join(",");

  return textCurrentValue !== textPreValue;
};
